import axios from "axios";

// All Cardano chain data (accounts, addresses, blocks, txs, assets, pools,
// epochs, metadata, network) is served by a separately-deployed API app —
// this is the base URL used by every data-fetching hook/widget/panel below,
// the API Reference drawer's "try it"/curl examples, and the header's
// Swagger/docs link.
export const BACKEND_URL = process.env.REACT_APP_EXTERNAL_API_URL;
export const API = `${BACKEND_URL}/api`;
// This job's OWN lightweight shell (health/info/docs + the external-status
// proxy) — distinct from BACKEND_URL above, which is the external Cardano
// data API every widget/panel fetches from.
export const LOCAL_API_URL = process.env.REACT_APP_BACKEND_URL;
// The API Reference drawer's curl examples/"Try it" always use the shared
// demo key — this is a pure data explorer, there's no on-site key signup.
// Getting your own key happens externally at https://gridscan.io/dashboard.
export const DEMO_KEY = process.env.REACT_APP_PROJECT_ID || "mainnet_demo_47c3b1e2";
export const PROJECT_ID = DEMO_KEY;

// Dedicated key for the explorer website's OWN traffic (widgets, search,
// detail views) — kept separate from the demo key used by the API Reference
// drawer, so real site traffic isn't mixed in with "someone testing the raw
// API" usage.
export const INTERNAL_KEY = process.env.REACT_APP_INTERNAL_PROJECT_ID || "explorer_internal_b697af8d";

export const CARDANO_NETWORK = (process.env.REACT_APP_CARDANO_NETWORK || "preprod").toLowerCase();
export const NETWORK_LABEL =
  CARDANO_NETWORK === "mainnet" ? "MAINNET · Cardano blockchain" : "PREPROD · Cardano testnet";

// Cross-links to the "other" network's deployment (this app is deployed once per
// network — see DEPLOYMENT.md). Empty string = not deployed yet, switcher shows
// that option as "coming soon" instead of a broken link.
export const NETWORK_LINKS = [
  {
    key: "mainnet",
    label: "Mainnet",
    url: process.env.REACT_APP_MAINNET_URL || "",
    active: CARDANO_NETWORK === "mainnet",
  },
  {
    key: "preprod",
    label: "PreProd",
    url: process.env.REACT_APP_PREPROD_URL || "",
    active: CARDANO_NETWORK === "preprod",
  },
];

// Axios instance that sends the project_id header on every request. Used by
// all site-driven data fetching (widgets, search, detail views) — always
// identifies as INTERNAL_KEY, not the visitor's own/demo key (see
// ApiReferenceDrawer, which overrides this per-request for "Try it").
// We send BOTH header names because some proxies (nginx with default
// `underscores_in_headers off`) silently strip headers containing underscores.
// `x-project-id` survives any proxy; `project_id` is kept for backwards-compat.
export const http = axios.create({
  headers: {
    "x-project-id": INTERNAL_KEY,
    project_id: INTERNAL_KEY,
  },
});

// The upstream Cardano data API is rate-limited (~10 req/window) and serves
// some asset endpoints from a cold cache: the first hit can time out with a
// 503/504, get a 429, or — when too many connections pile up — be dropped
// outright, which the browser surfaces as a bare "Network Error" (no
// response object). All of these are transient and recover on a quick retry
// (verified: a 503 on the first call becomes 200 on the second). This helper
// does a GET with automatic backoff on exactly those transient conditions so
// detail tabs don't flash a scary error on a single hiccup.
const isTransient = (e) => {
  // No response at all → network error / dropped connection / CORS-less gateway.
  if (!e.response) return true;
  return [429, 500, 502, 503, 504].includes(e.response.status);
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const getWithRetry = async (url, config = {}, opts = {}) => {
  const { retries = 3, baseDelay = 900, maxDelay = 6000, signal } = opts;  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await http.get(url, { timeout: 15000, signal, ...config });
    } catch (e) {
      if (attempt >= retries || !isTransient(e) || e.code === "ERR_CANCELED") throw e;
      // Respect Retry-After when the server sends it (seconds), else back off.
      const ra = Number(e.response?.headers?.["retry-after"]);
      const backoff = Number.isFinite(ra) && ra > 0
        ? Math.min(ra * 1000, maxDelay)
        : Math.min(baseDelay * 2 ** attempt, maxDelay);
      attempt += 1;
      await sleep(backoff);
    }
  }
};

export const ACCOUNT_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? [
        "stake1uxelhrqfulwdnmxuj0d2f73x7k35h7nkyvlpmwt0caw8jhg3jqts0",
        "stake1ux7njr9yqtr0ay8z9napemdy3xkt39uxwht7l5al02cds8c4ryack",
        "stake1uxn5hyavphxrtlwmtwss0duvqd2ypvzh8w2qt5rgk9pnzncfjwvem",
      ]
    : [
        "stake_test1upxue2rk4tp0e3tp7l0nmfmj6ar7y9yvngzu0vn7fxs9ags2apttt",
        "stake_test1uqhgtcy3p6fq4r3erflllxmmvw907vujg9gkzppcn3ja90c3zu3yy",
        "stake_test1urkaxwavpp37j083cvafwymnpmqm5wl6hre4ev99pcyt3tcvq0gns",
      ];

export const ADDRESS_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? [
        "addr1q8fwxda8mq09rzjcluxrep9wyrnfe4e8kuzqzkvcdn53vskmspx9w7e55ly30vqnw4a0eza3hj2hxnhhq6hqqh58gyvstww5ww",
        "addr1q8udzluw2pny3fl8nwpy0r094q4r8n4rxpye79d9ljrfzcqw8meglqj5fqw5q0446fh3rrtm5rqfatek7r735zkxyj5qel7gfr",
      ]
    : [
        "addr_test1qrq9nanwka0nphrjkx5jz9u2hwvf9ecguz0y0wsgaremdl2dej58d2kzlnzkra7l8knh9468ug2gexs9c7e8ujdqt63qrg35q8",
        "addr_test1vz09v9yfxguvlp0zsnrpa3tdtm7el8xufp3m5lsm7qxzclgmzkket",
      ];

export const ASSET_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? [
        "b863bc7369f46136ac1048adb2fa7dae3af944c3bbb2be2f216a8d4f42657272795369656e6e61",
        "12e65fa3585d80cba39dcf4f59363bb68b77f9d3c0784734427b1517475243",
      ]
    : [
        "e2bab64ca481afc5a695b7db22fd0a7df4bf930158dfa652fb33799953554d4d495441574152445344656669",
        "e2bab64ca481afc5a695b7db22fd0a7df4bf930158dfa652fb337999436f696e41",
      ];

export const BLOCK_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? ["latest", "6177509", "ed0c818c6266550c00927f8b4bad6e86df9325173181647e4f2450b3de7c5ea7"]
    : ["latest", "4659786", "ccdff319be5ad8ebeda342daef2a20bd6c65747f6b483cd967854725190f57d7"];

export const TX_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? [
        "68d30850944f85fc1a75b8c20f3e0958e5db8b18e9c24632017003fb2596c605",
        "2affd8203a754e6d732a0ac206e0b1f83aa7ae31bb2afe5681e862c82ccc3eaf",
        "e1098032f0307cadef7c57820f408f7e75058d05e2d58b3fa19b5530addbad41",
      ]
    : [
        "f19372baa3084f730a927fb680d9710081f93acd3430180b8228fbd8a9a26cf4",
        "8ca0b1828d4b61faec7c7ac02272caec17056bbdc69e1ff818e8ea925b14cb94",
        "a86d5246c1e5ce7d66446d0a68355abe6622545d8ffe7dd832a062f6cde010bd",
      ];

export const POOL_EXAMPLES =
  CARDANO_NETWORK === "mainnet"
    ? [
        "pool1z5uqdk7dzdxaae5633fqfcu2eqzy3a3rgtuvy087fdld7yws0xt",
        "pool1q80jjs53w0fx836n8g38gtdwr8ck5zre3da90peuxn84sj3cu0r",
        "pool1ddskftmsscw92d7vnj89pldwx5feegkgcmamgt5t0e4lkd7mdp8",
      ]
    : [
        "pool1945cfqzmy2qe2z59mfdwm7jwqtucncfee5xkd8f4wu97zr8xuys",
        "pool1kxtrz8evgdrwwrdajhwweng6k8eysjvqgn59r8a5wkqw6rlnvv6",
        "pool18svpptnxfctewpx7ntnmlazy4w9cr24f4rcq8smkvpggw600ql4",
      ];

export const EPOCH_EXAMPLES = CARDANO_NETWORK === "mainnet" ? ["latest", "287", "286"] : ["latest", "284", "283"];

export const METADATA_EXAMPLES = ["674", "721", "20"];

// Quick-pick chips shown in the Metadata mode's empty state — the most
// commonly-browsed CIP metadata labels.
export const METADATA_QUICK_PICKS = [
  { label: "674", desc: "Tx messages" },
  { label: "721", desc: "NFT metadata" },
  { label: "20", desc: "Fungible tokens" },
];

export const MODES = {
  accounts: {
    key: "accounts",
    label: "Accounts",
    subject: "stake address",
    placeholder: "Enter a stake address (stake1… or stake_test1…)",
    basePath: "/accounts",
    examples: ACCOUNT_EXAMPLES,
    defaultTab: "rewards",
  },
  addresses: {
    key: "addresses",
    label: "Addresses",
    subject: "address",
    placeholder: "Enter a Cardano address (addr1… or addr_test1…)",
    basePath: "/addresses",
    examples: ADDRESS_EXAMPLES,
    defaultTab: "txs",
  },
  assets: {
    key: "assets",
    label: "Assets",
    subject: "asset",
    placeholder: "Enter an asset unit (policy_id + asset_name in hex)",
    basePath: "/assets",
    examples: ASSET_EXAMPLES,
    defaultTab: "history",
  },
  blocks: {
    key: "blocks",
    label: "Blocks",
    subject: "block",
    placeholder: "Enter a block number, 64-char hash, or 'latest'",
    basePath: "/blocks",
    examples: BLOCK_EXAMPLES,
    defaultTab: "transactions",
  },
  transactions: {
    key: "transactions",
    label: "Transactions",
    subject: "tx hash",
    placeholder: "Enter a 64-char tx hash",
    basePath: "/txs",
    examples: TX_EXAMPLES,
    defaultTab: "flow",
  },
  pools: {
    key: "pools",
    label: "Pools",
    subject: "pool id",
    placeholder: "Enter a stake-pool ID (pool1…)",
    basePath: "/pools",
    examples: POOL_EXAMPLES,
    defaultTab: "history",
  },
  epochs: {
    key: "epochs",
    label: "Epochs",
    subject: "epoch",
    placeholder: "Enter an epoch number, or 'latest'",
    basePath: "/epochs",
    examples: EPOCH_EXAMPLES,
    defaultTab: "blocks",
  },
  metadata: {
    key: "metadata",
    label: "Metadata",
    subject: "metadata label",
    placeholder: "Enter a metadata label (e.g. 674, 721, 20)",
    basePath: "/metadata/txs/labels",
    examples: METADATA_EXAMPLES,
    defaultTab: "entries",
  },
};

export const HOME_FILTERS = [
  { key: "all", label: "All filters" },
  { key: "blocks", label: "Blocks" },
  { key: "epochs", label: "Epochs" },
  { key: "metadata", label: "Metadata" },
  { key: "stake_keys", label: "Stake Keys" },
  { key: "pools", label: "Pools" },
  { key: "tokens", label: "Tokens" },
  { key: "transactions", label: "Transactions" },
  { key: "addresses", label: "Addresses" },
  { key: "governance", label: "Governance" },
];

// Maps the home-page filter pill to the explorer mode key (or null = "coming soon").
export const FILTER_TO_MODE = {
  blocks: "blocks",
  stake_keys: "accounts",
  tokens: "assets",
  transactions: "transactions",
  addresses: "addresses",
  pools: "pools",
  epochs: "epochs",
  metadata: "metadata",
};

// Inverse — derive the home-page filter pill from the active explorer mode.
export const MODE_TO_FILTER = Object.entries(FILTER_TO_MODE).reduce((acc, [filterKey, modeKey]) => {
  if (modeKey) acc[modeKey] = filterKey;
  return acc;
}, {});
